import { useState, useCallback } from 'react';
import {
  PlanEmergencia,
  PlanEmergenciaForm,
} from '../types/PlanEmergencia';
import { findAll, save, update } from '../services/Service';
import { logger } from '../utils/logger';

export const usePlanEmergencia = () => {
  const [planEmergenciaSelected, setPlanEmergenciaSelected] =
    useState<PlanEmergencia>({
      id: 0,
      idSolicitud: 0,
      art: '',
      nroContrato: '',
      // Centro de Derivación 1
      nombre1: '',
      direccion1: '',
      // Centro de Derivación 2
      nombre2: '',
      direccion2: '',
      // Centro de Derivación 3
      nombre3: '',
      direccion3: '',
      // Roles de contingencia individuales
      incendio: '',
      rescateHombreAgua: '',
      fugaDerrame: '',
      accidentePersonal: '',
      rescateEC: '',
      rescateAltura: '',
      metodologiaAtencion: '',
      equipamientoEmergencia: '',
      personalEmergencia: [],
    });

  const [isLoading, setIsLoading] = useState(false);
  const [tieneRiesgoModeradoAlto, setTieneRiesgoModeradoAlto] =
    useState(false);

  // Función para obtener personal de emergencia
  const obtenerPersonalEmergencia = useCallback(
    async (idSolicitud: number) => {
      try {
        const response = await findAll(
          `planEmergenciaSolicitud/personal/${idSolicitud}`
        );

        if (response?.data) {
          setPlanEmergenciaSelected((prev) => ({
            ...prev,
            personalEmergencia: response.data,
          }));
          return response.data;
        }
        return [];
      } catch (error) {
        logger.error(
          'Error al obtener personal de emergencia:',
          logger.safeError(error)
        );
        return [];
      }
    },
    []
  );

  // Función para verificar si tiene riesgo moderado/alto
  const verificarRiesgoModeradoAlto = useCallback(
    async (idSolicitud: number) => {
      try {
        const response = await findAll(
          `planEmergenciaSolicitud/verificar-riesgo/${idSolicitud}`
        );
        const tieneRiesgo = response?.data === true;
        setTieneRiesgoModeradoAlto(tieneRiesgo);
        return tieneRiesgo;
      } catch (error) {
        logger.error('Error al verificar riesgo:', logger.safeError(error));
        setTieneRiesgoModeradoAlto(false);
        return false;
      }
    },
    []
  );

  // Función para convertir PlanEmergencia a PlanEmergenciaForm (ahora son iguales)
  const convertirAPlanEmergenciaForm = useCallback(
    (plan: PlanEmergencia): PlanEmergenciaForm => {
      return {
        id: plan.id,
        idSolicitud: plan.idSolicitud,
        art: plan.art,
        nroContrato: plan.nroContrato,
        // Campos individuales
        nombre1: plan.nombre1,
        direccion1: plan.direccion1,
        nombre2: plan.nombre2,
        direccion2: plan.direccion2,
        nombre3: plan.nombre3,
        direccion3: plan.direccion3,
        incendio: plan.incendio,
        rescateHombreAgua: plan.rescateHombreAgua,
        fugaDerrame: plan.fugaDerrame,
        accidentePersonal: plan.accidentePersonal,
        rescateEC: plan.rescateEC,
        rescateAltura: plan.rescateAltura,
        metodologiaAtencion: plan.metodologiaAtencion,
        equipamientoEmergencia: plan.equipamientoEmergencia,
        // personalEmergencia no se envía al backend (solo lectura)
      };
    },
    []
  );

  // Función para convertir PlanEmergenciaForm a PlanEmergencia (ahora son iguales)
  const convertirAPlanEmergencia = useCallback(
    (form: PlanEmergenciaForm): PlanEmergencia => {
      return {
        id: form.id,
        idSolicitud: form.idSolicitud,
        art: form.art,
        nroContrato: form.nroContrato,
        // Campos individuales
        nombre1: form.nombre1,
        direccion1: form.direccion1,
        nombre2: form.nombre2,
        direccion2: form.direccion2,
        nombre3: form.nombre3,
        direccion3: form.direccion3,
        incendio: form.incendio,
        rescateHombreAgua: form.rescateHombreAgua,
        fugaDerrame: form.fugaDerrame,
        accidentePersonal: form.accidentePersonal,
        rescateEC: form.rescateEC,
        rescateAltura: form.rescateAltura,
        metodologiaAtencion: form.metodologiaAtencion,
        equipamientoEmergencia: form.equipamientoEmergencia,
        // personalEmergencia viene del backend (solo lectura)
        personalEmergencia: form.personalEmergencia || [],
      };
    },
    []
  );

  // Función para buscar plan de emergencia por ID de solicitud
  const buscarPlanEmergenciaByIdSolicitud = useCallback(
    async (idSolicitud: number) => {
      try {
        setIsLoading(true);
        const response = await findAll(
          `planEmergenciaSolicitud/solicitud/${idSolicitud}`
        );

        if (response?.data) {
          // El backend siempre maneja campos individuales
          const planEmergencia = response.data;

          // Asegurar que tenemos las estructuras mínimas y el ID de solicitud
          setPlanEmergenciaSelected((prev) => ({
            ...planEmergencia,
            idSolicitud: idSolicitud,
            // Asegurar que siempre tengamos las estructuras mínimas
            personalEmergencia: planEmergencia.personalEmergencia
              ?.length
              ? planEmergencia.personalEmergencia
              : prev.personalEmergencia,
          }));
        } else {
          // Si no hay datos, asegurar que tenga el ID de solicitud
          setPlanEmergenciaSelected((prev) => ({
            ...prev,
            idSolicitud: idSolicitud,
          }));
        }
      } catch (error) {
        logger.error('Error al buscar plan de emergencia:', logger.safeError(error));
        // En caso de error, al menos establecer el ID de solicitud
        setPlanEmergenciaSelected((prev) => ({
          ...prev,
          idSolicitud: idSolicitud,
        }));
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Función para guardar o actualizar plan de emergencia
  const guardarPlanEmergencia = useCallback(
    async (planEmergenciaData: PlanEmergenciaForm) => {
      try {
        setIsLoading(true);
        let response: any;

        // Enviar directamente el formato de formulario al backend (con campos individuales)
        // if (planEmergenciaData.id === 0) {
        // Crear nuevo
        response = await save(
          'planEmergenciaSolicitud/guardar',
          planEmergenciaData
        );
        // } else {
        //   // Actualizar existente
        //   response = await update(
        //     'planEmergenciaSolicitud',
        //     planEmergenciaData
        //   );
        // }

        if (response?.data) {
          // El backend devuelve los datos guardados pero NO incluye personalEmergencia
          // Por eso preservamos el personalEmergencia que ya teníamos
          setPlanEmergenciaSelected((prev) => ({
            ...response.data,
            personalEmergencia: prev.personalEmergencia,
          }));
        }

        return response;
      } catch (error) {
        logger.error('Error al guardar plan de emergencia:', logger.safeError(error));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Función para actualizar centro de derivación
  const actualizarCentroDerivacion = useCallback(
    (index: number, campo: 'nombre' | 'direccion', valor: string) => {
      setPlanEmergenciaSelected((prev) => {
        const updates: Partial<PlanEmergencia> = {};

        if (index === 0) {
          if (campo === 'nombre') updates.nombre1 = valor;
          else updates.direccion1 = valor;
        } else if (index === 1) {
          if (campo === 'nombre') updates.nombre2 = valor;
          else updates.direccion2 = valor;
        } else if (index === 2) {
          if (campo === 'nombre') updates.nombre3 = valor;
          else updates.direccion3 = valor;
        }

        return {
          ...prev,
          ...updates,
        };
      });
    },
    []
  );

  // Función para actualizar rol de contingencia
  const actualizarRolContingencia = useCallback(
    (campo: string, valor: string) => {
      setPlanEmergenciaSelected((prev) => {
        const updates: Partial<PlanEmergencia> = {};

        switch (campo) {
          case 'incendio':
            updates.incendio = valor;
            break;
          case 'rescateHombreAgua':
            updates.rescateHombreAgua = valor;
            break;
          case 'fugaDerrame':
            updates.fugaDerrame = valor;
            break;
          case 'accidentePersonal':
            updates.accidentePersonal = valor;
            break;
          case 'rescateEC':
            updates.rescateEC = valor;
            break;
          case 'rescateAltura':
            updates.rescateAltura = valor;
            break;
        }

        return {
          ...prev,
          ...updates,
        };
      });
    },
    []
  );

  // Función para actualizar campos principales
  const actualizarCampo = useCallback(
    (campo: keyof PlanEmergencia, valor: any) => {
      setPlanEmergenciaSelected((prev) => ({
        ...prev,
        [campo]: valor,
      }));
    },
    []
  );

  // Función para resetear el plan de emergencia
  const resetearPlanEmergencia = useCallback(() => {
    setPlanEmergenciaSelected({
      id: 0,
      idSolicitud: 0,
      art: '',
      nroContrato: '',
      // Centro de Derivación 1
      nombre1: '',
      direccion1: '',
      // Centro de Derivación 2
      nombre2: '',
      direccion2: '',
      // Centro de Derivación 3
      nombre3: '',
      direccion3: '',
      // Roles de contingencia individuales
      incendio: '',
      rescateHombreAgua: '',
      fugaDerrame: '',
      accidentePersonal: '',
      rescateEC: '',
      rescateAltura: '',
      metodologiaAtencion: '',
      equipamientoEmergencia: '',
      personalEmergencia: [],
    });
  }, []);

  return {
    // Estado
    planEmergenciaSelected,
    isLoading,
    tieneRiesgoModeradoAlto,

    // Acciones
    buscarPlanEmergenciaByIdSolicitud,
    obtenerPersonalEmergencia,
    verificarRiesgoModeradoAlto,
    guardarPlanEmergencia,
    actualizarCentroDerivacion,
    actualizarRolContingencia,
    actualizarCampo,
    resetearPlanEmergencia,
    setPlanEmergenciaSelected,

    // Conversiones
    convertirAPlanEmergenciaForm,
    convertirAPlanEmergencia,
  };
};
